import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableSortLabel from "@mui/material/TableSortLabel"
import Typography from "@mui/material/Typography"
import Paper from "@mui/material/Paper"
import Checkbox from "@mui/material/Checkbox"
import * as dataServices from "../pages/services/dataServices"
import db from "../Firestore"
import { format } from "date-fns"
import { Box, Chip, Grid } from "@mui/material"
import firebase from "firebase/compat/app"
import { useHistory } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { selectComponentGridPagination } from "../redux/selectors"
import { setComponentGridPagination, setEditMode } from "../redux/actions"
import { selectComponentGridPageDocs } from "../redux/selectors"
import { setComponentGridPageDocs } from "../redux/actions"
import { selectComponentGridSelectedItems } from "../redux/selectors"
import { setComponentGridSelectedItems } from "../redux/actions"
import Controls from "./controls/Controls"
import * as tagServices from "../pages/services/tagServices"
import TagLabel from "./TagLabel"
import useAccountStatus from "./useAccountStatus"
import { spacing } from "../pages/services/styleServices"
import StyledLink from "./controls/StyledLink"
import * as colors from "@mui/material/colors"
import TagChip from "./TagChip"
import PageNo from "./PageNo"

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Name",
    sortable: true,
  },
  {
    id: "tags",
    numeric: false,
    disablePadding: true,
    label: "Tags",
    sortable: false,
  },
  {
    id: "created",
    numeric: false,
    disablePadding: false,
    label: "Created",
    sortable: true,
  },
]

const ComponentGrid = (props) => {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    selected,
    components,
    rowCount,
    onRequestSort,
  } = props

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  const componentsSelectedCount = components.filter((item) =>
    selected.find((s) => item.id === s.id)
  ).length
  const allComponentsOnPageSelected =
    componentsSelectedCount === rowCount && rowCount > 0
  const someComponentsOnPageSelected =
    componentsSelectedCount > 0 && componentsSelectedCount < rowCount

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={someComponentsOnPageSelected || rowCount === 0}
            checked={allComponentsOnPageSelected}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all components" }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={headCell.sortable && orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  {headCell.label}
                </Typography>
              </TableSortLabel>
            ) : (
              <Typography sx={{ fontWeight: "bold" }}>
                {headCell.label}
              </Typography>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

ComponentGrid.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
}

const styles = {
  root: {
    width: "100%",
    marginTop: spacing(2),
  },
  paper: {
    width: "100%",
    marginBottom: spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  navButtons: {
    display: "flex",
    flexDirection: "row",
    gap: 1,
  },
  gridContainer: {
    paddingLeft: "5px",
    paddingRight: "5px",
  },
  filterGrid: {
    paddingLeft: "15px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  tagsCell: {
    display: "flex",
    flexDirection: "row",
    gap: 1,
    flexWrap: "wrap",
  },
  link: {
    "& > a": {
      textDecoration: "none",
    },
  },
}

export default function EnhancedTable() {
  const selectedComponentItems = useSelector(selectComponentGridSelectedItems)
  const [selected, setSelected] = React.useState(selectedComponentItems)

  // 'next', or 'prev'. Used to inform pagination logic
  const [direction, setDirection] = useState("")

  const [dense, setDense] = React.useState(true)

  const pag = useSelector(selectComponentGridPagination)
  const [pagination, setPagination] = useState(pag)

  const dispatch = useDispatch()

  const history = useHistory()

  // Filter
  const [providerId, setProviderId] = useState(pag.provider_id)

  //const [tagsFilter, setTagsFilter] = useState("")

  const [providers, setProviders] = useState([])

  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  const { isNewComponentActive } = useAccountStatus()

  const [components, setComponents] = useState([])

  const [user, setUser] = useState()

  const [accountId, setAccountId] = useState()

  const [tagsFilter, setTagsFilter] = useState("")

  useEffect(() => {
    const unsub = firebase.auth().onAuthStateChanged((user) => {
      console.log("### user changed", user)
      setUser(user)

      if (user !== null) {
        user.getIdTokenResult(false).then((token) => {
          console.log("### setting account id", token.claims.account_id)
          setAccountId(token.claims.account_id)
        })
      }
    })

    return unsub
  }, [])

  const handleTagDelete = (tagToDelete) => {
    const newTags = pagination.tags.filter(
      (tag) =>
        !(tag.type === tagToDelete.type && tag.label === tagToDelete.label)
    )
    const updatedPagination = {
      ...pagination,
      tags: newTags,
    }
    setPagination(updatedPagination)
    dispatch(setComponentGridPagination(updatedPagination))
  }

  const handleTagChange = (event) => {
    const value = event.target.value || ""
    setTagsFilter(value)
  }

  const handleTagKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      setPageDocs([])

      console.log("handleTagKeyPress", tagsFilter)

      const enteredTags = tagServices.parseTags(tagsFilter)
      const newTags = enteredTags.filter(
        (tag) =>
          pagination.tags.find(
            (pt) => pt.type === tag.type && pt.label === tag.label
          ) === undefined
      )

      console.log("newTags", { newTags, enteredTags })

      if (newTags.length > 0) {
        console.log("%cadding tag to filter", "color:lightGreen", newTags)
        const updatedTags = [...pagination.tags, ...newTags]

        const updatedPagination = {
          ...pagination,
          page: 0,
          tags: updatedTags,
        }

        setDirection("")
        setPagination(updatedPagination)
        dispatch(setComponentGridPagination(updatedPagination))

        console.log("%cset pagination", "color:yellow", updatedPagination)
      }
      setTagsFilter("")
    }
  }

  const pgDocs = useSelector(selectComponentGridPageDocs)
  const [pageDocs, setPageDocs] = useState(pgDocs)

  const COLLECTION_NAME = "components"

  const [maxModified, setMaxModified] = useState()

  // Listen for changes

  useEffect(() => {
    if (user === undefined || accountId === undefined) {
      return
    }

    const whereClauseLog = []

    let query = db
      .collection(COLLECTION_NAME)
      .where("modified", ">=", dataServices.localTimestamp())
      .orderBy("modified", "desc")

    whereClauseLog.push(
      "modified >= " +
        dataServices.localTimestamp() +
        " [" +
        dataServices.localTimestamp().toDate() +
        "]"
    )

    let unsubscribe

    firebase
      .auth()
      .currentUser.getIdTokenResult()
      .then((token) => {
        query = query.where("account_id", "==", token.claims.account_id)
        whereClauseLog.push("account_id == " + token.claims.account_id)

        //console.log('whereClause for listener', whereClauseLog)

        let newMaxModified = null
        unsubscribe = query.onSnapshot((querySnapshot) => {
          querySnapshot.docChanges().forEach((change) => {
            if (
              newMaxModified === null ||
              change.doc.data().modified.seconds > newMaxModified.seconds
            ) {
              // trigger refresh
              newMaxModified = change.doc.data().modified
            }
          })
        })

        if (newMaxModified !== null) {
          setMaxModified(newMaxModified)
        }
      })
    return unsubscribe // gets called on unmount
  }, [user, accountId])

  // Get most recent modified record

  useEffect(() => {
    if (user === undefined || accountId === undefined) {
      return
    }

    console.log("=== get max modified date ===")

    let query = db.collection(COLLECTION_NAME)

    let unsubscribe

    firebase
      .auth()
      .currentUser.getIdTokenResult()
      .then((token) => {
        query = query.where("account_id", "==", token.claims.account_id)

        query = query.orderBy("modified", "desc").limit(30)

        unsubscribe = query.onSnapshot((querySnapshot) => {
          let newMaxModified = null
          querySnapshot.docChanges().forEach((change) => {
            if (
              newMaxModified === null ||
              change.doc.data().modified.seconds > newMaxModified.seconds
            ) {
              console.log(
                "=== max modified ===",
                change.doc.id,
                change.doc.data().name,
                change.doc.data().modified
              )
              newMaxModified = change.doc.data().modified
            }
          })
          if (newMaxModified !== null) {
            setMaxModified(newMaxModified)
          }
        })
      })
    return unsubscribe
  }, [user, accountId])

  const updatePageDocs = () => {
    if (components.length > 0 && direction !== "prev") {
      const newPageDocs = [...pageDocs]

      const newPageDoc = {
        first: components[0].doc,
        last: components[components.length - 1].doc,
      }

      newPageDocs[pagination.page] = newPageDoc
      setPageDocs(newPageDocs)
      dispatch(setComponentGridPageDocs(newPageDocs))
    }
  }

  useEffect(() => {
    updatePageDocs(components)
  }, [components])

  // Load components

  useEffect(() => {
    if (user === undefined || accountId === undefined) {
      return
    }

    if (maxModified === undefined) {
      return
    }

    console.log("(1) load components", {
      accountId,
      user,
      maxModified,
      pagination,
    })

    const queryMods = []

    let query = db.collection(COLLECTION_NAME)

    if (!firebase.auth().currentUser) {
      return
    }

    firebase
      .auth()
      .currentUser.getIdTokenResult()
      .then((token) => {
        switch (token.claims.account_type) {
          case "client":
            query = query.where("account_id", "==", token.claims.account_id)
            queryMods.push("where account_id == " + token.claims.account_id)
            break

          case "provider":
            query = query.where(
              "provider_account_id",
              "==",
              token.claims.account_id
            )
            queryMods.push(
              "where provider_account_id == " +
                token.claims.account_id +
                " (account id=>" +
                token.claims.account_id +
                ", account_type=>" +
                token.claims.account_type +
                ")"
            )
            break

          default:
            throw new Error("Unknown account type " + token.claims.account_type)
        }

        // if (providerId !== "") {
        //     query = query.where("provider_id", "==", providerId)
        //     queryMods.push("provider_id = " + providerId)
        // }

        if (pagination.tags.length > 0) {
          console.log("%cload stories, pagination", "color:yellow", {
            pagination,
          })
          const tagsExpr = tagServices.toSearchExpression(pagination.tags)
          query = query.where("tags_index", "array-contains-any", tagsExpr)
          queryMods.push(`tags_index contains any ${tagsExpr}`)
        }

        // Get current page of components

        query = query.orderBy(pagination.orderBy, pagination.order)
        queryMods.push(
          "order by " + pagination.orderBy + " " + pagination.order
        )
        query = query.orderBy(
          firebase.firestore.FieldPath.documentId(),
          pagination.order
        )
        queryMods.push("order by doc id " + pagination.order)

        // console.groupCollapsed('pagination')
        // console.log('pagination', pagination)
        // console.log('direction', direction)
        // console.log('pageDocs', pageDocs)
        // console.groupEnd()

        if (pagination.page > 0 && direction !== "prev") {
          // Use pageDocs if available, i.e. if we've gone forward, then back, then forward again through collection.
          // But if not found, it just means this is the first time we've clicked Next through the collection
          if (pageDocs[pagination.page - 1]) {
            const lastDoc = pageDocs[pagination.page - 1].last
            //console.log('startAfter', lastDoc.id)
            query = query.startAfter(lastDoc)
            queryMods.push(
              "start after last doc on previous page " + lastDoc.id
            )
          }
        } else if (direction === "prev") {
          if (!pageDocs[pagination.page]) {
            console.error("Cant find pagedocs for page", pagination.page)
          }
          query = query.startAt(pageDocs[pagination.page].first)
          queryMods.push(
            "start at 1st doc on page " + pageDocs[pagination.page].first.id
          )
        }

        console.log("queryMods", queryMods)
        console.groupEnd()

        query = query.limit(rowsPerPage)

        dataServices
          .loadData("(Load component grid)", query)
          .then((components) => applyComponents(components))
          .then(updatePageDocs())

        console.groupEnd()
      })
  }, [accountId, user, maxModified, pagination])

  const applyComponents = (components) => {
    setComponents(components)
  }

  const handleRequestSort = (event, property) => {
    const isAsc = pagination.orderBy === property && pagination.order === "asc"

    const updatedPagination = {
      tags: [],
      ...pagination,
      page: 0,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
      provider_id: providerId,
    }

    setPagination(updatedPagination)
    dispatch(setComponentGridPagination(updatedPagination))

    setPageDocs([])
    dispatch(setComponentGridPageDocs([]))

    setDirection("")
  }

  // Select all on component grid

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const allSelected = components.map((n) => {
        return {
          id: n.id,
          label: n.name,
        }
      })

      console.log("allSelected", allSelected)
      console.log("selected", selected)

      // Find newly selected ids

      const newSelected = [...selected]

      const newAdditions = allSelected.filter(
        (item) => selected.find((s) => item.id === s.id) === undefined
      )
      console.log("new additions", newAdditions)
      newAdditions.forEach((item) => newSelected.push(item))

      setSelected(newSelected)
      dispatch(setComponentGridSelectedItems(newSelected))
    } else {
      // Remove all components from selected
      const allDeselected = components.map((n) => {
        return {
          id: n.id,
          label: n.name,
        }
      })
      const newSelected = selected.filter(
        (item) => allDeselected.find((s) => item.id === s.id) === undefined
      )
      setSelected(newSelected)
      dispatch(setComponentGridSelectedItems(newSelected))
    }
  }

  const deselectComponent = (id) => {
    const existing = selected.find((item) => item.id === id)
    if (existing) {
      const newSelected = selected.filter((item) => item.id !== id)
      setSelected(newSelected)
      dispatch(setComponentGridSelectedItems(newSelected))
    }
  }

  const handleNewComponent = (event) => {
    event.preventDefault()
    dispatch(setEditMode(true))
    history.push("/component")
  }

  const handleClick = (event, name, id) => {
    if (event.target.checked) {
      const newSelected = [...selected]
      newSelected.push({
        id: id,
        label: name,
      })
      setSelected(newSelected)
      dispatch(setComponentGridSelectedItems(newSelected))
    } else {
      deselectComponent(id)
    }
  }

  const handlePageNav = (pageChange) => {
    const newPage = pagination.page + pageChange
    if (newPage >= 0) {
      setDirection(pageChange === 1 ? "next" : "prev")

      const updatedPagination = {
        ...pagination,
        page: newPage,
      }

      setPagination(updatedPagination)
      dispatch(setComponentGridPagination(updatedPagination))
    }
  }

  const isSelected = (id) =>
    selected.find((item) => item.id === id) !== undefined

  return (
    <>
      <Grid container direction="row" spacing={2} sx={styles.filterGrid}>
        <Grid item>
          <Controls.TextInput
            name="tags_filter"
            label="Filter Tags"
            helperText="Enter a tag in the form type:label, e.g. Domain:Customer"
            value={tagsFilter}
            onChange={handleTagChange}
            onKeyPress={handleTagKeyPress}
          />
        </Grid>
        <Grid item>
          {pagination &&
            pagination.tags &&
            pagination.tags.map((tag) => (
              <Chip
                key={`${tag.type}:${tag.label}`}
                size="small"
                label={<TagLabel tag={tag} />}
                onDelete={() => handleTagDelete(tag)}
              />
            ))}
        </Grid>
      </Grid>

      <Box sx={styles.root}>
        <Paper sx={styles.paper}>
          <TableContainer>
            <Table
              sx={styles.table}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
              aria-label="Components"
            >
              <ComponentGrid
                numSelected={selected.length}
                selected={selected}
                components={components}
                order={pagination.order}
                orderBy={pagination.orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={components.length}
              />
              <TableBody>
                {components.map((row, index) => {
                  const isItemSelected = isSelected(row.id)
                  const labelId = `enhanced-table-checkbox-${index}`

                  let providerName = ""
                  if (row.provider_id !== "") {
                    const provider = providers.find(
                      (provider) => provider.id === row.provider_id
                    )
                    if (provider) {
                      providerName = provider.name
                    }
                  }

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.name, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </TableCell>

                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        <StyledLink
                          to={"/component/" + row.id}
                          onClick={() => dispatch(setEditMode(false))}
                        >
                          {row.name}
                        </StyledLink>
                      </TableCell>

                      <TableCell>
                        <Box sx={styles.tagsCell}>
                          {row.tags &&
                            row.tags.map((tag) => (
                              <TagChip
                                key={`${tag.type}-${tag.label}`}
                                tag={tag}
                              />
                            ))}
                        </Box>
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap={true}>
                          {format(row.created.toDate(), "dd-M-yy")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Box sx={styles.navButtons}>
          <Controls.Button
            size="small"
            disabled={pagination.page === 0}
            onClick={() => handlePageNav(-1)}
            text="Prev"
          />

          <Controls.Button
            size="small"
            disabled={components.length < rowsPerPage}
            onClick={() => handlePageNav(1)}
            text="Next"
          />

          <Controls.Button
            size="small"
            onClick={(event) => handleNewComponent(event)}
            text="New Component"
            variant="contained"
            disabled={!isNewComponentActive(components.length)}
            tooltip="Create a new component to upload Architecture files"
          />
        </Box>
        <PageNo pageNo={pagination.page + 1} />
      </Box>
    </>
  )
}
